import { Link } from 'react-router-dom';
import LegalClause from './LegalClause';
import { BUSINESS } from '../../constants/businessDetails';

/**
 * Cancellation & Refund policy body content — no page chrome, so it renders
 * both as the full /refund-policy page (pages/RefundPolicyPage.tsx) and as a
 * Trust Center tab on the logged-out homepage. One of the five pages Razorpay
 * requires a merchant site to carry before it enables UPI Autopay / cards /
 * Subscriptions. Must agree with Terms clause 10; if one changes, change both.
 *
 * `onOpenTerms` / `onOpenContact`: when set (the homepage tab), the
 * cross-references switch the open document instead of leaving the page —
 * the same shape as TermsContent's `onOpenPrivacy`.
 */
/** The page's overline and title, shared by /refund-policy and the homepage tab. */
export const REFUND_POLICY_HEADING = {
  overline: 'Cancellation & Refund',
  title: 'Cancellation and Refund Policy',
} as const;

interface RefundPolicyContentProps {
  onOpenTerms?: () => void;
  onOpenContact?: () => void;
}

export default function RefundPolicyContent({ onOpenTerms, onOpenContact }: RefundPolicyContentProps) {
  const termsLink = onOpenTerms ? (
    <button type="button" onClick={onOpenTerms} className="underline">
      Terms of Service
    </button>
  ) : (
    <Link to="/terms" className="underline">Terms of Service</Link>
  );
  const contactLink = onOpenContact ? (
    <button type="button" onClick={onOpenContact} className="underline">
      Contact page
    </button>
  ) : (
    <Link to="/contact" className="underline">Contact page</Link>
  );

  return (
    <>
      <p className="text-sm text-text-secondary mb-8">
        {BUSINESS.brand} is operated by <strong>{BUSINESS.legalName}</strong>. Last updated 3 November 2026.
        Read together with our {termsLink}.
      </p>

      <LegalClause id="r1" n="1" title="Free trial">
        <p>Every new account gets a 30-day free trial. No payment details are taken for the trial and nothing is charged when it ends; you choose whether to subscribe.</p>
      </LegalClause>

      <LegalClause id="r2" n="2" title="Plans and renewal">
        <p>2.1 Prices are shown before payment, exclusive of GST; the amount charged includes 18% GST and is shown in full at checkout. A GST tax invoice is emailed for every payment.</p>
        <p>2.2 <strong>Annual plans</strong> are paid once for twelve months and do not renew automatically.</p>
        <p>2.3 <strong>Monthly plans</strong> renew each month only where you have authorised a recurring payment (UPI Autopay or card mandate). Otherwise access runs for the month paid for and ends unless you renew.</p>
      </LegalClause>

      <LegalClause id="r3" n="3" title="Cancellation">
        <p>3.1 You can cancel a monthly plan at any time from your Profile page. Cancellation stops future renewals; your access continues until the end of the period already paid for.</p>
        <p>3.2 An annual plan needs no cancellation — it simply ends at the close of its twelve months.</p>
      </LegalClause>

      <LegalClause id="r4" n="4" title="Refunds">
        <p>4.1 Fees for a period that has started are not refunded in part, except where the law requires it.</p>
        <p>4.2 If you were charged twice, or charged an amount you did not authorise, write to us within 7 days of the charge. Once verified, the full amount is refunded to the original payment method; banks usually credit it within 5–7 working days.</p>
      </LegalClause>

      <LegalClause id="r5" n="5" title="Contact">
        <p>
          Refund or cancellation questions: <strong>{BUSINESS.accountsEmail}</strong>. See also our{' '}
          {contactLink}.
        </p>
      </LegalClause>
    </>
  );
}
